import { concat, fn } from '@ember/helper';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { addDays, addYears, getUnixTime, startOfDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import { Changeset } from 'ember-changeset';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import set from 'ember-set-helper/helpers/set';
import { and, eq, not, or } from 'ember-truth-helpers';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import FileInput from 'garaje/components/file-input';
import TagsInput from 'garaje/components/tags-input';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import preventDefault from 'garaje/helpers/prevent-default';
import type BlacklistFilterModel from 'garaje/models/blacklist-filter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { empty, match, not as notMacro, or as orMacro, and as andMacro } from 'macro-decorators';

interface ExpirationOption {
  label: string;
  value: string;
  days?: number;
}

const KB = 1024;
const NAME_REGEX = /(\S+ )+\S+/; // at least 2 non-space characters with a space in between
const PHONE_REGEX = /^.*(\d.*){7,}$/; // at least 7 numbers

const EXPIRATION_OPTIONS: ExpirationOption[] = [
  { label: '7 days', value: '7_days', days: 7 },
  { label: '14 days', value: '14_days', days: 14 },
  { label: '30 days', value: '30_days', days: 30 },
  { label: '60 days', value: '60_days', days: 60 },
  { label: '90 days', value: '90_days', days: 90 },
  { label: '180 days', value: '180_days', days: 180 },
  { label: '1 year', value: '1_year', days: 365 },
  { label: 'Never', value: 'never' },
  { label: 'Custom date', value: 'custom' },
];

interface BlocklistFilterModalSignature {
  Args: {
    blocklistFilter: BlacklistFilterModel;
    close: () => void;
    delete?: () => void;
    isSaving: boolean;
    maxPhotoSizeKb: number;
    /**
     * boolean indicating whether the blocklist should be read-only or not
     */
    readOnly?: boolean;
    save: (photoFile: File | null) => void;
  };
}

export default class BlocklistFilterModal extends Component<BlocklistFilterModalSignature> {
  elementId = guidFor(this);

  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  nameRegex = NAME_REGEX;
  phoneRegex = PHONE_REGEX;

  get hasBlocklistEnhancements(): boolean {
    return this.featureFlags.isEnabled('feat-blocklist-expiration-and-pause');
  }

  get locationTimezone(): string {
    return this.state.currentLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  @tracked changeset;
  @tracked dataUrl: string | ArrayBuffer | null = null;
  @tracked photoThumb: string | null = null;
  @tracked maxPhotoSizeKb;
  @tracked photoFile: File | null = null;
  @tracked isPhotoFileTooBig = false;
  @tracked didAttemptSubmit = false;
  @tracked uploadPhotoFileInputId = null;
  @tracked updatePhotoFileInputId = null;
  @tracked didEditFullName = false;
  @tracked blocklistFilter;
  @tracked selectedExpirationOption: ExpirationOption;
  @tracked showDatePickerInDropdown = false;

  // dataUrl comes first so that an updated preview shows instead of an existing thumb
  @orMacro('dataUrl', 'photoThumb') photoPreview!: string;
  @orMacro('fullNameIsValid', 'emptyFullName') fullNameIsValidOrEmpty!: boolean;
  @orMacro('hasEmptyMatchingFields', 'emptyReason') isMissingRequiredFields!: boolean;
  @orMacro('isMissingRequiredFields', 'fullNameHasError') canNotSubmit!: boolean;
  @notMacro('fullNameIsValidOrEmpty') fullNameHasError!: boolean;
  @notMacro('hasEmptyMatchingFields') hasAtLeastOneMatchingField!: boolean;
  @notMacro('emptyReason') hasAReason!: boolean;
  @match('changeset.fullName', NAME_REGEX) fullNameIsValid!: boolean;
  @empty('changeset.reason') emptyReason!: boolean;
  @empty('changeset.fullName') emptyFullName!: boolean;
  @empty('changeset.aliases') emptyAliases!: boolean;
  @empty('changeset.emails') emptyEmails!: boolean;
  @empty('changeset.phoneNumbers') emptyPhoneNumbers!: boolean;
  @empty('changeset.otherKeywords') emptyOtherKeywords!: boolean;
  @andMacro('emptyFullName', 'emptyAliases', 'emptyEmails', 'emptyPhoneNumbers', 'emptyOtherKeywords')
  hasEmptyMatchingFields!: boolean;

  constructor(owner: unknown, args: BlocklistFilterModalSignature['Args']) {
    super(owner, args);
    // use a changeset so that the form isn't mutating the underlying model prior to validation/saving
    // otherwise edits show up in the table view behind the modal
    const { blocklistFilter, maxPhotoSizeKb } = this.args;
    this.blocklistFilter = blocklistFilter;
    this.changeset = Changeset(blocklistFilter);
    this.dataUrl = blocklistFilter?.photo.dataUrl;
    this.photoThumb = blocklistFilter?.photo.thumb;
    this.maxPhotoSizeKb = maxPhotoSizeKb || 500;

    // Initialize expiration option based on existing expiresAt value
    this.selectedExpirationOption = this.determineExpirationOption(blocklistFilter?.expiresAt);

    this.metrics.trackJobEvent('Security - Blocked Person Modal Opened', {
      block_list_filter_id: this.blocklistFilter?.id,
    });
  }

  get expirationOptions(): ExpirationOption[] {
    return EXPIRATION_OPTIONS;
  }

  get isCustomDateSelected(): boolean {
    return this.selectedExpirationOption?.value === 'custom';
  }

  get expirationDisplayText(): string {
    const option = this.selectedExpirationOption;
    if (option?.value === 'custom' && this.changeset.expiresAt) {
      // Format in location timezone so admin sees the intended expiration date
      return formatInTimeZone(this.changeset.expiresAt, this.locationTimezone, 'MMM d, yyyy');
    }
    return option?.label || 'Never';
  }

  get dropdownContentClass(): string {
    // Minimal styling - just z-index and pointer-events
    // Width is controlled by scoped CSS in template
    return 'z-[999999999] pointer-events-none';
  }

  get selectedExpirationDate(): Date | null {
    return this.changeset.expiresAt ?? null;
  }

  get expirationDateLimits(): { startDate: number; endDate: undefined } {
    // Use location timezone to ensure calendar evaluates dates consistently
    // Falls back to browser timezone if location is not available
    const timezone = this.state.currentLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zonedNow = toZonedTime(new Date(), timezone);
    const today = startOfDay(zonedNow);
    return {
      startDate: getUnixTime(today), // Can only select today or future dates
      endDate: undefined, // No max limit
    };
  }

  get expiresAtValue(): Date | undefined {
    return this.changeset.expiresAt ?? undefined;
  }

  determineExpirationOption(expiresAt: Date | null | undefined): ExpirationOption {
    if (!expiresAt) {
      return EXPIRATION_OPTIONS.find((opt) => opt.value === 'never')!;
    }
    // If there's an existing date, default to custom date option
    return EXPIRATION_OPTIONS.find((opt) => opt.value === 'custom')!;
  }

  @action
  handleExpirationOptionClick(option: ExpirationOption, closeDropdown: () => void): void {
    this.selectedExpirationOption = option;

    if (option.value === 'never') {
      this.changeset.expiresAt = null;
      this.showDatePickerInDropdown = false;
      closeDropdown();
    } else if (option.value === 'custom') {
      // Show the date picker in the dropdown
      this.showDatePickerInDropdown = true;
    } else if (option.days) {
      // Calculate the expiration date based on days from location's "today"
      // This ensures "7 days" means 7 days in the location's timezone, not the browser's
      const locationNow = toZonedTime(new Date(), this.locationTimezone);
      const expirationDate = option.value === '1_year' ? addYears(locationNow, 1) : addDays(locationNow, option.days);
      // Set time to 11:59 PM in the location's timezone (person remains blocked until end of day)
      const dateAt1159PM = new Date(
        expirationDate.getFullYear(),
        expirationDate.getMonth(),
        expirationDate.getDate(),
        23,
        59,
        59,
      );
      this.changeset.expiresAt = fromZonedTime(dateAt1159PM, this.locationTimezone);
      this.showDatePickerInDropdown = false;
      closeDropdown();
    }
  }

  @action
  handleCalendarDateSelect(closeDropdown: () => void, selectedDate: Date): void {
    // Set time to 11:59 PM in the location's timezone (person remains blocked until end of day)
    const dateAt1159PM = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      23,
      59,
      59,
    );
    this.changeset.expiresAt = fromZonedTime(dateAt1159PM, this.locationTimezone);
    this.showDatePickerInDropdown = false;
    closeDropdown();
  }

  @action
  handleDropdownClose(): void {
    this.showDatePickerInDropdown = false;
  }

  @action
  willDestroy(...args: Parameters<Component['willDestroy']>): void {
    super.willDestroy(...args);

    this.metrics.trackJobEvent('Security - Blocked Person Modal Closed', {
      block_list_filter_id: this.blocklistFilter?.id,
    });
  }

  updatePhoto(attrs: { dataUrl: string | ArrayBuffer | null; thumb?: string | null; original?: string | null }): void {
    const { changeset } = this;
    const photoCopy = {
      ...changeset.photo,
      ...attrs,
    };
    changeset.photo = photoCopy;
  }

  @action
  setPhotoFile(file: File): void {
    if (file.size < this.maxPhotoSizeKb * KB) {
      this.photoFile = file;
      this.isPhotoFileTooBig = false;
    } else {
      this.isPhotoFileTooBig = true;
    }
  }

  @action
  setPhotoDataUrl(dataUrl: string | ArrayBuffer | null): void {
    // https://github.com/poteto/ember-changeset#updates
    // we can't do mut in the template because nested changeset mut doesn't work
    // once we upgrade to ember-changeset 3.x we can use the changeset-set template helper to do this
    // this.changeset.set('photo.dataUrl', dataUrl); // eslint-disable-line ember/use-ember-get-and-set
    this.dataUrl = dataUrl;
    this.updatePhoto({ dataUrl });
  }

  @action
  removePhoto(): void {
    this.updatePhoto({
      dataUrl: null,
      thumb: null,
      original: null,
    });

    this.photoFile = null;
    this.dataUrl = null;
    this.photoThumb = null;
  }

  @action
  handleSubmit(): void {
    this.didAttemptSubmit = true;
    this.metrics.trackJobEvent(
      `Security - Blocked Person ${
        <boolean>(<unknown>this.blocklistFilter?.isNew) ? 'Creation' : 'Update'
      } Button Clicked`,
      {
        button_text: 'Save',
        form_state: this.canNotSubmit ? 'invalid' : 'valid',
      },
    );
    if (this.canNotSubmit) return;

    this.changeset.execute();
    this.args.save(this.photoFile);
  }

  @action
  handleDelete(): void {
    this.metrics.trackJobEvent('Security - Blocked Person Delete Button Clicked', { button_text: 'Delete' });
    this.args.delete?.();
  }

  @action
  closeModal(): void {
    this.metrics.trackJobEvent('Security - Blocked Person Close Modal Button Clicked', { button_text: 'X' });
    this.args.close();
  }

  <template>
    <ModalDialog
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="ember-modal-dialog--header-modal !max-w-[600px]"
    >
      <FocusTrapper>

        <UiButton
          data-test-close-blocklist-filter-modal
          class="btn btn--small btn--outline btn--cancel float-right mr-8 mt-8"
          {{on "click" this.closeModal}}
        >
          <span aria-hidden="true">&times;</span>
          <span class="sr-only">close</span>
        </UiButton>

        <div class="p-8">
          <h2 data-test-blocklist-filter-modal-heading>
            {{#if @readOnly}}
              View
              {{! @glint-expect-error - ember data types cause issues }}
            {{else if this.blocklistFilter.isNew}}
              New
            {{else}}
              Edit
            {{/if}}
            blocked person
          </h2>

          <form {{on "submit" (preventDefault this.handleSubmit)}} data-test-blocklist-filter-modal-form>
            <fieldset class="my-4">
              <legend class="float-left box-border w-4/12 font-bold pl-0">Photo</legend>

              <div class="float-left box-border w-8/12 flex items-center">
                {{#if (and this.photoPreview (not this.isPhotoFileTooBig))}}
                  <div
                    data-test-blocklist-filter-photo-preview
                    class="border shadow animated fadeIn bg-cover bg-center"
                    style={{htmlSafe (concat "width:100px;height:100px;background-image:url(" this.photoPreview ");")}}
                  >
                    <span class="sr-only">Current photo</span>
                  </div>
                  {{#unless @readOnly}}
                    <FileInput
                      data-test-blocklist-filter-photo-update
                      @accept="image/png,image/gif,image/jpeg"
                      @onLoadFile={{this.setPhotoFile}}
                      @onLoadDataUrl={{this.setPhotoDataUrl}}
                      @onId={{set this "updatePhotoFileInputId"}}
                      class="hidden"
                    />
                    <label
                      for={{this.updatePhotoFileInputId}}
                      class="label-reset inline-block btn btn--hoverFill btn--smaller ml-2 animated fadeIn"
                    >
                      <span class="icon-pencil-dark-full small"></span>
                      Update
                    </label>

                    <UiButton
                      data-test-blocklist-filter-photo-remove
                      class="btn btn--hoverFill btn--smaller ml-2 animated fadeIn"
                      {{on "click" this.removePhoto}}
                    >
                      <span class="icon-trash small"></span>
                      Remove
                    </UiButton>
                  {{/unless}}
                {{else}}
                  {{#unless @readOnly}}
                    <FileInput
                      data-test-blocklist-filter-photo-upload
                      @accept="image/png,image/gif,image/jpeg"
                      @onLoadFile={{this.setPhotoFile}}
                      @onLoadDataUrl={{this.setPhotoDataUrl}}
                      @onId={{set this "uploadPhotoFileInputId"}}
                      class="hidden"
                    />
                  {{/unless}}
                  <label
                    for={{this.uploadPhotoFileInputId}}
                    class="inline-block border focus:border-red-50 shadow text-center animated fadeIn
                      {{if this.isPhotoFileTooBig 'border-red-50'}}"
                    style="width:100px;height:100px;line-height:100px;"
                  >
                    <img
                      src={{if @readOnly "/assets/images/profile.svg" "/assets/images/add.svg"}}
                      alt=""
                      class={{if @readOnly "h-8 w-8 align-middle" "h-3 w-3 align-text-bottom"}}
                      role="none"
                    />
                    {{unless @readOnly "Upload"}}
                  </label>
                  {{#if this.isPhotoFileTooBig}}
                    <p class="mb-0 text-red-50 pl-4 text-xs">
                      Please upload a photo smaller than
                      {{this.maxPhotoSizeKb}}kb.
                    </p>
                  {{/if}}
                {{/if}}
              </div>
            </fieldset>

            <fieldset class="my-4">
              <legend class="pl-0 pb-4 font-bold">Matching fields</legend>

              <label for={{concat this.elementId "fullName"}} class="float-left box-border w-4/12 mt-1 pt-2">Full name</label>
              <div class="float-left box-border w-8/12 {{if (and this.didEditFullName this.fullNameHasError) 'mb-2'}}">
                <input
                  data-test-blocklist-filter-full-name
                  disabled={{@readOnly}}
                  id={{concat this.elementId "fullName"}}
                  type="text"
                  autocomplete="off"
                  value={{this.changeset.fullName}}
                  class={{if (and this.didEditFullName this.fullNameHasError) "error"}}
                  {{on "input" (pick "target.value" (set this.changeset "fullName"))}}
                  {{on "blur" (fn (mut this.didEditFullName) true)}}
                />
                {{#if (and this.didEditFullName this.fullNameHasError)}}
                  <div class="bottom-error" data-test-blocklist-filter-full-name-error>
                    Please enter a first and last name.
                  </div>
                {{/if}}
              </div>

              <label
                id={{concat this.elementId "-aliases-label"}}
                class="float-left box-border w-4/12 mt-1 pt-2"
              >Aliases</label>
              <div data-test-blocklist-filter-aliases>
                <TagsInput
                  @disabled={{@readOnly}}
                  @ariaLabelledBy={{concat this.elementId "-aliases-label"}}
                  @bgReset={{true}}
                  @onChange={{fn (mut this.changeset.aliases)}}
                  @selected={{this.changeset.aliases}}
                  @tagLabel="alias"
                  @validationType="format"
                  @validationOptions={{hash regex=this.nameRegex}}
                  @validationDescription="Must include a first and last name."
                />
              </div>

              <label id={{concat this.elementId "-email-label"}} class="float-left box-border w-4/12 mt-1 pt-2">Email
                Addresses</label>
              <div data-test-blocklist-filter-emails>
                <TagsInput
                  @disabled={{@readOnly}}
                  @ariaLabelledBy={{concat this.elementId "-email-label"}}
                  @bgReset={{true}}
                  @onChange={{fn (mut this.changeset.emails)}}
                  @selected={{this.changeset.emails}}
                  @tagLabel="email"
                  @validationType="format"
                  @validationOptions={{hash type="email"}}
                />
              </div>

              <label id={{concat this.elementId "-phone-label"}} class="float-left box-border w-4/12 mt-1 pt-2">Phone
                numbers</label>
              <div data-test-blocklist-filter-phone-numbers>
                <TagsInput
                  @disabled={{@readOnly}}
                  @ariaLabelledBy={{concat this.elementId "-phone-label"}}
                  @bgReset={{true}}
                  @onChange={{fn (mut this.changeset.phoneNumbers)}}
                  @selected={{this.changeset.phoneNumbers}}
                  @tagLabel="phone number"
                  @validationType="format"
                  @validationOptions={{hash regex=this.phoneRegex}}
                />
              </div>

              <label
                id={{concat this.elementId "-keywords-label"}}
                class="float-left box-border w-4/12 mt-1 pt-2"
              >Keywords</label>
              <div data-test-blocklist-filter-keywords>
                <TagsInput
                  @disabled={{@readOnly}}
                  @ariaLabelledBy={{concat this.elementId "-keywords-label"}}
                  @bgReset={{true}}
                  @onChange={{fn (mut this.changeset.otherKeywords)}}
                  @selected={{this.changeset.otherKeywords}}
                  @tagLabel="keyword"
                />
              </div>
            </fieldset>

            <fieldset class="my-4">
              <legend class="pl-0 pb-4 font-bold">Details</legend>

              <label for={{concat this.elementId "description"}} class="float-left box-border w-4/12 mt-1 pt-2">
                Physical description
              </label>
              <div class="float-left box-border w-8/12">
                <textarea
                  disabled={{@readOnly}}
                  id={{concat this.elementId "description"}}
                  class="static short resize-y"
                  autocomplete="off"
                  value={{this.changeset.description}}
                  {{on "change" (pick "target.value" (set this.changeset "description"))}}
                  data-test-blocklist-filter-description
                />
              </div>

              <label
                for={{concat this.elementId "reason"}}
                class="float-left box-border w-4/12 mt-1 pt-2 requiredField"
              >
                Reason for blocking
              </label>
              <div class="float-left box-border w-8/12">
                <textarea
                  disabled={{@readOnly}}
                  id={{concat this.elementId "reason"}}
                  class={{concat "static short resize-y" (if (and this.didAttemptSubmit this.emptyReason) " error")}}
                  autocomplete="off"
                  value={{this.changeset.reason}}
                  {{on "change" (pick "target.value" (set this.changeset "reason"))}}
                  data-test-blocklist-filter-reason
                />
              </div>

              {{#if this.hasBlocklistEnhancements}}
                {{! NEW FIELD - Feature: feat-blocklist-expiration-and-pause }}
                <label class="float-left box-border w-4/12 mt-1 pt-2">Expires after</label>
                <div class="float-left box-border w-8/12" data-test-blocklist-filter-expires-after>
                  <BasicDropdown
                    @disabled={{@readOnly}}
                    @verticalPosition="auto"
                    @matchTriggerWidth={{true}}
                    @renderInPlace={{false}}
                    @onClose={{this.handleDropdownClose}}
                    as |dropdown|
                  >
                    <dropdown.Trigger
                      class="w-full flex items-center justify-between px-3 py-2 text-base border border-carbon-20 rounded-md bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40
                        {{if dropdown.isOpen 'border-gem-50' ''}}
                        {{if @readOnly 'opacity-50 cursor-not-allowed'}}"
                      data-test-blocklist-filter-expiration-dropdown
                    >
                      <span>{{this.expirationDisplayText}}</span>
                      <svg height="5" viewBox="0 0 8 5" width="8" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="m6.798 0-2.798 2.885-2.798-2.885-1.202 1.24 3.4 3.503a.83.83 0 0 0 1.2 0l3.4-3.503z"
                          fill="#3F4450"
                          fill-opacity=".5"
                        />
                      </svg>
                    </dropdown.Trigger>

                    <dropdown.Content class={{this.dropdownContentClass}}>
                      {{#if this.showDatePickerInDropdown}}
                        {{! Calendar view - matches Figma: 276px width, 12px padding, shadow/md }}
                        {{! template-lint-disable use-ui-card }}
                        <div
                          class="w-[276px] p-3 bg-white border border-carbon-20 rounded-lg pointer-events-auto"
                          style="box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10);"
                          data-test-blocklist-filter-expires-at
                        >
                          {{! template-lint-disable no-forbidden-elements }}
                          {{!
                            Scoped styles for blocklist expiration calendar (different from .blocklist-calendar):
                            - Uses 30x30px selected date (vs 36x36px in .blocklist-calendar) per Figma spec
                            - Targets via data-test attribute to avoid affecting other calendars
                            - BasicDropdown width override via :has() selector (not possible in global CSS)
                            - Hex colors used directly (can't use theme() in template <style> tags)
                              #3F4450 = carbon-70, #8A8F9E = carbon-40, #4e4eda = gem-60, #ffffff = white
                          }}
                          <style>
                            /* Position-aware margins for floating effect */ .ember-basic-dropdown-content--below
                            [data-test-blocklist-filter-expires-at] { margin-top: 8px; }
                            .ember-basic-dropdown-content--above [data-test-blocklist-filter-expires-at] {
                            margin-bottom: 8px; } .ember-basic-dropdown-content--below > div[role="listbox"] {
                            margin-top: 4px; } .ember-basic-dropdown-content--above > div[role="listbox"] {
                            margin-bottom: 4px; } /* Override BasicDropdown's width to match calendar (276px) */
                            .ember-basic-dropdown-content:has([data-test-blocklist-filter-expires-at]) { width: 276px
                            !important; background-color: transparent !important; } /* Constrain calendar to exact width
                            */ [data-test-blocklist-filter-expires-at] .calendar-grid { width: 252px !important; /* 7
                            days × 36px */ max-width: 252px !important; } [data-test-blocklist-filter-expires-at]
                            .calendar-grid table { width: 100% !important; table-layout: fixed !important; } /* Remove
                            border-top separator line from calendar header */ [data-test-blocklist-filter-expires-at]
                            .calendar-grid th { border-top: none !important; } /* Day numbers should be dark - matches
                            Figma #3F4450 */ [data-test-blocklist-filter-expires-at] .calendar-row td { color: #3F4450
                            !important; font-weight: 500 !important; font-size: 14px !important; line-height: 20px
                            !important; text-align: center !important; } /* Disabled/out-of-limits dates - light gray,
                            no strikethrough */ [data-test-blocklist-filter-expires-at] .calendar-row td.out-of-limits,
                            [data-test-blocklist-filter-expires-at] .calendar-row td.disabled { color: #8A8F9E
                            !important; text-decoration: none !important; } /* Selected date styling - 30x30px circle,
                            perfectly centered */ [data-test-blocklist-filter-expires-at] .selected-date {
                            background-color: #4e4eda !important; color: #ffffff !important; padding: 0 !important;
                            width: 30px !important; height: 30px !important; display: flex !important; align-items:
                            center !important; justify-content: center !important; border-radius: 9999px !important; }
                            [data-test-blocklist-filter-expires-at] .bg-carbon-10.selected-date { background-color:
                            #4e4eda !important; } [data-test-blocklist-filter-expires-at] .text-carbon-70.selected-date
                            { color: #ffffff !important; }
                          </style>
                          <EnvoyOneUpCalendar
                            @selectedDate={{this.selectedExpirationDate}}
                            @onSelectDate={{fn this.handleCalendarDateSelect dropdown.actions.close}}
                            @dateLimits={{this.expirationDateLimits}}
                          />
                        </div>
                      {{else}}
                        {{! Dropdown list - matches trigger width }}
                        <div
                          class="flex flex-col gap-px p-1 max-h-80 overflow-y-auto bg-white border border-carbon-20 rounded-md shadow-lg pointer-events-auto"
                          role="listbox"
                        >
                          {{#each this.expirationOptions as |option|}}
                            <div
                              role="option"
                              aria-selected={{eq option.value this.selectedExpirationOption.value}}
                              class="p-2 text-base leading-6 cursor-pointer rounded-md hover:bg-gem-5 flex items-center justify-between
                                {{if (eq option.value this.selectedExpirationOption.value) 'bg-gem-5 text-gem-60'}}"
                              data-test-expiration-option={{option.value}}
                              {{on "click" (fn this.handleExpirationOptionClick option dropdown.actions.close)}}
                            >
                              <span>{{option.label}}</span>
                              {{#if (eq option.value this.selectedExpirationOption.value)}}
                                <img src="/assets/images/checkmark-gem-60.svg" alt="" class="w-[14px] h-[14px]" />
                              {{/if}}
                            </div>
                          {{/each}}
                        </div>
                      {{/if}}
                    </dropdown.Content>
                  </BasicDropdown>
                </div>
              {{/if}}

            </fieldset>

            {{#unless @readOnly}}
              <UiButton
                data-test-blocklist-filter-save
                @type="submit"
                class="btn btn--brand"
                disabled={{or @isSaving (and this.didAttemptSubmit this.canNotSubmit)}}
              >
                {{#if @isSaving}}
                  Saving...
                {{else}}
                  Save
                {{/if}}
              </UiButton>
            {{/unless}}

            {{#if (and this.didAttemptSubmit this.isMissingRequiredFields)}}
              <p class="inline-block mb-0 text-red-50 pl-4 text-xs" data-test-blocklist-filter-save-error>
                {{#if (and this.hasEmptyMatchingFields this.emptyReason)}}
                  Please fill at least one matching field and provide a reason for blocking.
                {{else if this.hasEmptyMatchingFields}}
                  Please fill at least one matching field.
                {{else if this.emptyReason}}
                  Please provide a reason for blocking.
                {{/if}}
              </p>
            {{/if}}

            {{#if (and (not this.blocklistFilter.isNew) (not @readOnly))}}
              <UiButton
                data-test-blocklist-filter-delete
                class="btn btn--hoverFill float-right"
                disabled={{@isSaving}}
                {{on "click" this.handleDelete}}
              >
                <span class="icon-trash"></span>
                Delete
              </UiButton>
            {{/if}}

          </form>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
